import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { useTokens } from '../../shared/theme';
import {
  beginGoogleSignin,
  completeGoogleSignin,
  getPendingGoogleSignin,
  googleSigninConfigured,
  hasPortalSession,
  portalAuthHandoffFromReferrer,
  requestMagicLink,
  resolvePortalHome,
  resumePortalSession,
} from '../../shared/services/auth';
import { getPublicBranding } from '../../shared/services/institutions.js';
import { clampAccent } from '../../shared/branding.js';
import { notifySubtleError, notifySuccess } from '../../shared/toast.js';
import { withDevPortalAccessToken } from '../../shared/devPortalSession.js';
import Spinner from '../../shared/Spinner.jsx';
import GoogleButton from '../../shared/GoogleButton.jsx';
import AuthScreen from '../AuthScreen.jsx';
import { marketingHomeUrl, schoolPortalUrl } from '../portalUrl.js';
import { usePortalDocumentTitle } from '../usePortalDocumentTitle.js';

const replaceLocation = (url) => { window.location.replace(url); };

/**
 * The shared OAuth-hop page, served on portal.coffeechats.me/auth. A school
 * subdomain stages a single-use browser handoff and opens this fixed /auth URL
 * so the auth cookie is set on the parent domain `.coffeechats.me`; once Google completes we send the visitor
 * back to the environment-correct school host (`<slug>.localhost:3100` in
 * local development, `https://<slug>.coffeechats.me` in production).
 *
 * Outbound leg: the consumed handoff starts the PKCE flow
 * (beginGoogleSignin), stashing the slug + a CSRF state nonce in sessionStorage
 * and redirecting to Google.
 *
 * Return leg (?code=&state=): verify the state nonce, exchange the code via
 * POST /signin (server sets the .coffeechats.me cookie), then hop to the school
 * subdomain. Google's own ?error= and any failure reset the card to the plain
 * sign-in screen and explain themselves in a toast.
 *
 * A visitor who arrives with no school context at all (no subdomain handoff,
 * no stashed pending sign-in) gets a generic, unbranded "Continue with
 * Google" - never a directory of every partnered school. After that sign-in
 * completes, `resolvePortalHome` asks the server which school (if any) this
 * account administers and we hop there, exactly as the branded path does.
 *
 * Open-redirect guard: the destination slug is validated with sanitizeReturnSlug
 * on both legs: a valid, non-reserved tenant slug, never a URL or path.
 */
function humanError(err) {
  if (err?.message === 'oauth_state_mismatch') {
    return 'Your sign-in session expired. Please reopen your school link and try again.';
  }
  if (err?.response?.status === 409) {
    return 'An account with this email already exists. Sign in on coffeechats.me first, then reopen your school link.';
  }
  return 'Sign-in failed. Please reopen your school link and try again.';
}

export default function AuthHop({ onRedirect = replaceLocation }) {
  const t = useTokens();
  const [params] = useSearchParams();
  const code = params.get('code');
  const googleError = params.get('error');
  const returnedState = params.get('state');
  const isReturnLeg = !!code || !!googleError;
  // A school host that already proved its slug doesn't resolve sends us here
  // with ?notice=unknown_school. The handoff below reads the sending school off
  // document.referrer — which on this path is that same dead slug — so without
  // this guard we would auto-start Google for a school that doesn't exist and
  // hop the visitor straight back to the host that just rejected them. Dropping
  // the handoff lands them on the generic sign-in, where the toast is visible.
  const unknownSchool = params.get('notice') === 'unknown_school';
  const [handoff] = useState(() => {
    return (isReturnLeg || unknownSchool) ? null : portalAuthHandoffFromReferrer();
  });
  const [pendingSignin] = useState(() => { return getPendingGoogleSignin(); });
  const outboundSlug = handoff?.slug || null;
  const returnPath = handoff?.returnPath || pendingSignin?.returnPath || '/overview';

  // 'idle' | 'checking' (resuming an existing session) | 'starting' (hopping out
  // to Google) | 'exchanging' (return leg, trading the code for a session)
  const [phase, setPhase] = useState(isReturnLeg ? 'exchanging' : (outboundSlug ? 'checking' : 'idle'));

  // EVERY Google wait renders on the card with a busy button, including the
  // return leg. That leg is a fresh page load, so it used to get a full-screen
  // "Signing you in..." panel — but the visitor's last sight before Google was
  // this card, and coming back to a different screen reads as having landed
  // somewhere new rather than as one continuous sign-in. Same card, busy
  // button, dead email field: the wait looks like what it is.
  const googleBusy = phase === 'checking' || phase === 'starting' || phase === 'exchanging';
  // Email magic-link state (2026-07-21): mirrors the main /login page — the
  // card swaps in place to a check-your-email state after a successful send.
  const [emailValue, setEmailValue] = useState('');
  const [sentEmail, setSentEmail] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [institution, setInstitution] = useState(null);
  const [brandingResolved, setBrandingResolved] = useState(false);
  const [brandSlug] = useState(() => { return outboundSlug || pendingSignin?.slug || null; });
  const destinationSlug = outboundSlug || brandSlug;
  const sessionCheckStartedRef = useRef(false);
  const exchangeStartedRef = useRef(false);
  const titleSchoolName = institution?.branding?.displayName || institution?.name;
  usePortalDocumentTitle(titleSchoolName);

  // Every failure resets the card to the plain sign-in screen and says why in a
  // toast. The old inline error state ended in a "Try again" button wired
  // straight to startGoogle, which hops to Google with no chooser: Google
  // silently re-approves the same account and drops the visitor back on the
  // identical error. Landing on the sign-in card instead lets them pick a
  // different account, switch to the email link, or leave.
  const fail = useCallback((message) => {
    setPhase('idle');
    notifySubtleError(message);
  }, []);

  // Explain the bounce on the screen the visitor actually lands on. This is a
  // toast rather than the inline 'error' phase because the wrong turn is the
  // URL they arrived from, not this page — sign-in here is still perfectly
  // usable. The school they typed is deliberately not echoed back: it is
  // visitor-authored text, and reflecting it would let a crafted subdomain put
  // arbitrary words in our own UI. toast dedupes by message, so StrictMode's
  // double-invoked effect still renders one.
  useEffect(() => {
    if (!unknownSchool) { return; }
    notifySubtleError('We couldn’t find that school portal. Sign in and we’ll take you to yours.');
  }, [unknownSchool]);

  useEffect(() => {
    let active = true;
    if (!brandSlug) { return undefined; }
    getPublicBranding(brandSlug)
      .then((data) => {
        if (!active) { return; }
        const rawAccent = data?.branding?.accentColor;
        setInstitution({
          ...data,
          accent: rawAccent ? clampAccent(rawAccent) : null,
        });
      })
      .catch(() => { if (active) { setInstitution(null); } })
      .finally(() => { if (active) { setBrandingResolved(true); } });
    return () => { active = false; };
  }, [brandSlug]);

  // `progress` picks which waiting state covers the hop to Google. Callers must
  // never pass this straight to onClick: a MouseEvent would land in `progress`.
  const startGoogle = useCallback(async (progress = 'starting') => {
    if (!googleSigninConfigured()) {
      fail('Sign-in is temporarily unavailable.');
      return;
    }
    setPhase(progress);
    try {
      await beginGoogleSignin(destinationSlug, returnPath);
      // Navigation happens inside beginGoogleSignin; nothing after this runs.
    } catch {
      fail('Could not start sign-in. Please try again.');
    }
  }, [destinationSlug, fail, returnPath]);

  // Wrapper, not a direct onClick binding: startGoogle's first parameter is the
  // progress phase, so handing it to onClick would land a MouseEvent there.
  const startGoogleFromCard = useCallback(() => { startGoogle(); }, [startGoogle]);

  // The button lives on <school>.coffeechats.me. Once it sends a visitor here,
  // continue directly to Google instead of asking them to click the same action
  // a second time. Wait for the public school identity first so the outbound
  // transition has the same branded treatment as the return leg. The ref
  // prevents React StrictMode from starting two flows.
  //
  // Exception: a stashed OAuth flow that never completed means the visitor
  // already left for Google once and came back without finishing (browser
  // back / closed the chooser). Auto-starting again would trap them in a
  // redirect loop, so wait for an explicit click instead.
  useEffect(() => {
    if (isReturnLeg || !outboundSlug || !brandingResolved || sessionCheckStartedRef.current) { return; }
    sessionCheckStartedRef.current = true;
    (async () => {
      try {
        const accessToken = await resumePortalSession();
        if (accessToken && await hasPortalSession(outboundSlug, accessToken)) {
          onRedirect(withDevPortalAccessToken(
            schoolPortalUrl(outboundSlug, window.location, returnPath),
            accessToken,
          ));
          return;
        }
        if (pendingSignin) {
          setPhase('idle');
          return;
        }
        await startGoogle();
      } catch {
        fail('Could not confirm your sign-in. Please try again.');
      }
    })();
  }, [brandingResolved, fail, isReturnLeg, onRedirect, outboundSlug, pendingSignin, returnPath, startGoogle]);

  // A bfcache restore can resurrect this page frozen in its "Opening Google..."
  // state after the visitor backs out of Google. Settle it on the explicit
  // sign-in screen instead of a stuck spinner (or a re-bounce to Google).
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted && getPendingGoogleSignin()) {
        sessionCheckStartedRef.current = true;
        setPhase('idle');
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => { window.removeEventListener('pageshow', onPageShow); };
  }, []);

  // Return leg: verify the CSRF nonce + exchange the code, then hop to the
  // school subdomain. On any failure, drop into the inline error state.
  useEffect(() => {
    if (!isReturnLeg || exchangeStartedRef.current) { return undefined; }
    // The OAuth flow record is single-use. React Strict Mode intentionally
    // replays effects in development, so let only the first effect invocation
    // consume it and exchange Google's one-time authorization code.
    exchangeStartedRef.current = true;
    if (googleError) {
      // A cancel on Google's side is a decision, not a failure. Land on the
      // branded sign-in screen (school + Continue with Google), not an error.
      notifySubtleError('Sign-in was cancelled.');
      setPhase('idle');
      return undefined;
    }
    (async () => {
      try {
        const { slug: exchangedSlug, accessToken, returnPath: completedPath } = await completeGoogleSignin({
          code, returnedState,
        });
        // The generic entry point stashed no slug; resolve which school (if
        // any) this account administers instead of ever listing them.
        const slug = exchangedSlug || (await resolvePortalHome(accessToken)).slug;
        if (!slug) {
          fail('We couldn’t match your Google account to a Coffee Chats school. Ask your school’s administrator for an invite.');
          return;
        }
        if (!(await hasPortalSession(slug, accessToken))) {
          fail('Sign-in did not complete. Please try again.');
          return;
        }
        onRedirect(withDevPortalAccessToken(
          schoolPortalUrl(slug, window.location, completedPath),
          accessToken,
        ));
      } catch (err) {
        fail(humanError(err));
      }
    })();
    return undefined;
  }, [isReturnLeg, googleError, code, returnedState, fail, onRedirect]);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim());

  // The emailed link lands on the school host when we know it (so the staff
  // member returns to their branded portal), else on this shared auth origin.
  const magicLinkReturnOrigin = () => {
    return destinationSlug
      ? new URL(schoolPortalUrl(destinationSlug, window.location)).origin
      : window.location.origin;
  };

  const sendMagicLink = async (email) => {
    setEmailSending(true);
    try {
      await requestMagicLink({
        email: email.trim(),
        returnOrigin: magicLinkReturnOrigin(),
        portalSlug: destinationSlug || '',
      });
      return true;
    } catch (err) {
      notifySubtleError(err?.response?.data?.error || 'Could not send the email. Please try again.');
      return false;
    } finally {
      setEmailSending(false);
    }
  };

  // Both waits own the whole card: sending a link blocks Google, and hopping to
  // Google blocks the email form, so neither can be started twice over.
  const emailDisabled = emailSending || googleBusy;

  const handleEmailContinue = async () => {
    if (!emailLooksValid || emailDisabled) { return; }
    if (await sendMagicLink(emailValue)) {
      setSentEmail(emailValue.toLowerCase().trim());
    }
  };

  const handleResend = async () => {
    if (emailSending) { return; }
    if (await sendMagicLink(sentEmail)) {
      notifySuccess('Email sent');
    }
  };

  const heading = (
    <>
      {/* Sizes mirror the student login headline (32/40 on phones, 46/54 from
          md — same 768px cut as the client's breakpoints.tablet — with its
          -0.8px tracking) so the two sign-in pages read at the same scale. */}
      <h1
        className="text-center text-[32px] leading-[40px] tracking-[-0.8px] md:text-[46px] md:leading-[54px]"
        style={{ fontFamily: t.fonts.display, fontWeight: 500, color: t.heading }}
      >
        Your university portal
      </h1>
      {/* The subline measures 465px and the shell column caps at 460px, so it
          orphans "team" onto a second line. Breaking out 16px each side (only
          from sm up, where the column is wide enough to absorb it) lets it sit
          on one line while staying centered; it still wraps on phones. */}
      <p
        className="mt-3 text-center text-[19px] sm:-mx-4"
        style={{ color: t.body, fontFamily: t.fonts.display }}
      >
        Adoption, outcomes, and seats for your career team
      </p>
    </>
  );

  const card = (children) => {
    return (
      <div
        className="mt-8 rounded-2xl border p-6 shadow-[0_10px_34px_rgba(42,23,16,0.06)]"
        style={{ backgroundColor: t.surface, borderColor: t.border }}
      >
        {children}
      </div>
    );
  };

  if (sentEmail) {
    return (
      <AuthScreen institution={institution}>
        {heading}
        {card(
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            {/* "If an account exists" rather than asserting a send. Only
                portal admins are mailed a link, and the server will not say
                who qualifies — so promising an email that may never arrive
                sends people to wait on an empty inbox. */}
            <p className="text-[15px] leading-relaxed" style={{ color: t.muted, fontFamily: t.fonts.body }}>
              If an account exists for
              <br />
              <span className="font-semibold" style={{ color: t.heading }}>{sentEmail}</span>
              <br />
              we’ve sent it a sign-in link.
            </p>
            <button
              type="button"
              onClick={handleResend}
              className={`underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${emailSending ? 'opacity-55' : ''}`}
              style={{ color: t.accent || '#E14E2C', fontFamily: t.fonts.body }}
            >
              Resend email
            </button>
            <button
              type="button"
              // Clear the field too. "Use a different email" is a correction,
              // so returning to a form still holding the address they are
              // trying to replace means retyping over it.
              onClick={() => { setSentEmail(null); setEmailValue(''); }}
              className={`underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
              style={{ color: t.accent || '#E14E2C', fontFamily: t.fonts.body }}
            >
              Use a different email
            </button>
          </div>
        )}
      </AuthScreen>
    );
  }

  return (
    <AuthScreen institution={institution}>
      {heading}
      {card(
        <>
          {/* disabled while the magic link sends: the comment on emailDisabled
              claims each wait blocks the other, but only half of it was wired,
              so Google was still clickable mid-send. */}
          <GoogleButton onClick={startGoogleFromCard} loading={googleBusy} disabled={emailSending} />

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1" style={{ backgroundColor: t.border }} />
            <span className="text-[12px] tracking-[0.1em]" style={{ color: t.muted, fontFamily: t.fonts.body }}>OR</span>
            <div className="h-px flex-1" style={{ backgroundColor: t.border }} />
          </div>

          <input
            type="email"
            value={emailValue}
            onChange={(event) => { setEmailValue(event.target.value); }}
            onKeyDown={(event) => { if (event.key === 'Enter') { handleEmailContinue(); } }}
            placeholder="Enter your email"
            autoComplete="email"
            disabled={emailDisabled}
            className={`min-h-[48px] w-full rounded-[10px] border bg-white px-4 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${emailDisabled ? 'cursor-not-allowed opacity-55' : ''}`}
            style={{ borderColor: t.border, color: t.body, fontFamily: t.fonts.body }}
          />

          <button
            type="button"
            onClick={handleEmailContinue}
            disabled={!emailLooksValid || emailDisabled}
            aria-busy={emailSending || undefined}
            className={`mt-3 flex min-h-[48px] w-full items-center justify-center rounded-[10px] text-base font-medium transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing} ${(!emailLooksValid || emailDisabled) ? 'cursor-not-allowed opacity-55' : ''}`}
            style={{ backgroundColor: '#111111', color: '#FFFBF6', fontFamily: t.fonts.body }}
          >
            {emailSending ? <Spinner size={20} /> : 'Continue with email'}
          </button>

          {destinationSlug ? (
            <p
              className="mt-4 text-center text-[13px]"
              style={{ color: t.muted, fontFamily: t.fonts.mono }}
            >
              {new URL(schoolPortalUrl(destinationSlug)).host}
            </p>
          ) : (
            <p
              className="mt-4 text-center text-[13px] leading-relaxed"
              style={{ color: t.muted, fontFamily: t.fonts.body }}
            >
              We’ll take you straight to your school’s Coffee Chats portal.
            </p>
          )}
        </>
      )}
      <p
        className="mt-5 text-center text-[13px] leading-[1.4]"
        style={{ color: t.muted, fontFamily: t.fonts.body }}
      >
        By continuing, you agree to our{' '}
        <a
          href={`${marketingHomeUrl()}/terms-of-service`}
          target="_blank"
          rel="noreferrer"
          className={`underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href={`${marketingHomeUrl()}/privacy-policy`}
          target="_blank"
          rel="noreferrer"
          className={`underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
        >
          Privacy Policy
        </a>
        .
      </p>
    </AuthScreen>
  );
}
